import React, { useMemo } from 'react'
import { ReactComponent as Announce } from '../../images/icons/sales.svg'
import { ReactComponent as ChevronRight } from '../../images/icons/chevron_right.svg'
import { ReactComponent as LinkIcon } from '../../images/icons/connect_link_icon.svg'
import { ReactComponent as FBIcon } from '../../images/facebook_ads_logo.svg'
import { ReactComponent as TradeDeskIcon } from '../../images/icons/audience_tradedesk.svg'
import { ReactComponent as GoogleLogo } from '../../images/icons/google_icon.svg'
import { ReactComponent as Cars } from '../../images/icons/audience_car.svg'
import { ReactComponent as DataBar } from '../../images/icons/chart.svg'
import { ReactComponent as Product } from '../../images/icons/product.svg'
import { ReactComponent as DLLogo } from '../../images/dl_logo_favicon.svg'
import { ReactComponent as AmazonIcon } from '../../images/icons/audience_amazon.svg'
import { Button, Pill } from '@demandlocal/components'
import { useNavigate } from 'react-router-dom'
import { useDealership } from '../../store/zustand/dealershipStore'
import { formatCurrency, formatNumber } from '../../lib/utils/formatNumber'
import { useGetVehiclesByCampaignId } from '../../api/dealership/campaigns/useGetVehiclesByCampaignId'
import { getImageURL } from '../../lib/utils/getImageURL'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { match } from 'ts-pattern'
import { VENDOR } from '../../lib/utils/vendors'
import { z } from 'zod'
import { isElementOfType } from '../../lib/utils/isElementOfType'
import {
  ManagedCampaignSchema,
  SelfServiceCampaignSchema,
} from '../../lib/schemas/campaignSchemas'
import { useTranslation } from 'react-i18next'

type Props = {
  item: z.infer<typeof ManagedCampaignSchema> | z.infer<typeof SelfServiceCampaignSchema>
  isSelfService?: boolean
  children: React.ReactNode
}

const CampaignOverviewCard = (props: Props): Array<unknown> => {
  const { children } = props

  const filteredChildren = React.Children.toArray(children).filter((child) => {
    return (
      isElementOfType(child, CampaignOverviewCard.SelfService) ||
      isElementOfType(child, CampaignOverviewCard.Managed)
    )
  })

  return filteredChildren
}

const SelfService = (props: {
  item: z.infer<typeof SelfServiceCampaignSchema>
}): JSX.Element => {
  const { item } = props
  const dealership = useDealership()
  const navigate = useNavigate()
  const campaign = {
    _id: item?._id,
    campaign_status: item?.vendor_campaign?.status_name,
    name: item?.vendor_campaign?.name,
    start_date: item?.vendor_campaign?.start_date,
    end_date: item?.vendor_campaign?.end_date,
    vendor_id: item?.vendor_id,
    vendor_campaign_id: item?.vendor_campaign?.id,
    account_id: item?.vendor_campaign?.account_id,
    account_name: item?.vendor_campaign?.account_name,
    impressions: item?.campaign_stats?.reduce((acc, curr) => acc + curr.impressions, 0),
    clicks: item?.campaign_stats?.reduce((acc, curr) => acc + curr.clicks, 0),
  }
  const { t } = useTranslation('routes')

  const linkedProductIconSelfService = match(Number(campaign.vendor_id))
    .with(VENDOR.Google, () => <GoogleLogo className="h-[25px] w-[25px]" />)
    .with(VENDOR.Facebook, () => <FBIcon className="h-[25px] w-[25px]" />)
    .otherwise(() => <DLLogo className="h-[25px] w-[25px]" />)
  return (
    <div
      className={classNames('flex flex-col border-2 rounded-lg font-Spartan p-4', {
        'bg-yellow-secondary/10 border-dashed border-yellow-secondary':
          campaign.campaign_status === 'pending',
      })}>
      <div className={'flex flex-row justify-between p-2 items-center'}>
        <div className={'flex flex-row items-center gap-3'}>
          <div
            className={'flex justify-center items-center bg-gray-200 rounded-full p-2'}>
            <Announce className="h-6 w-6" />
          </div>
          <span className={'font-bold line-clamp-2'}>
            [{campaign?.account_name}] {campaign?.name}
          </span>
        </div>
        <div className="uppercase pl-5">
          <Pill type={campaign.campaign_status === 'active' ? 'success' : 'warning'}>
            {campaign.campaign_status}
          </Pill>
        </div>
      </div>
      <div className={'flex flex-row text-xs px-2 pb-2'}>
        {dayjs(campaign.start_date).format('MMMM D')} -{' '}
        {dayjs(campaign.end_date).format('MMMM D')}
      </div>
      <div className={' px-2'}>
        <Pill type={'info-purple'}>Self service</Pill>
      </div>
      <div
        className={
          'flex flex-row justify-between mx-2 font-Spartan my-2 pb-2 border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex flex-row items-center ml-2'}>
          <div className={'mr-2 justify-center items-center'}>
            <LinkIcon className={'fill-black stroke-black h-5 w-5'} />
          </div>
          <span className={'mr-2 justify-start font-medium text-xs text-primary-xiketic'}>
            Linked To
          </span>
        </div>
        <div className={'flex justify-end text-sm'}>{linkedProductIconSelfService}</div>
      </div>
      <div
        className={
          'flex flex-row justify-between items-center mx-2 font-Spartan my-2 pb-2  border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex gap-2 flex-row items-center ml-2'}>
          <div className={'flex justify-center items-center'}>
            <DataBar className={'h-5 w-5'} />
          </div>
          <div
            className={
              'flex mr-2 justify-start font-medium text-xs text-primary-xiketic'
            }>
            Impressions
          </div>
        </div>
        <div className={'flex mr-2 justify-end font-medium text-xs text-primary-xiketic'}>
          {formatNumber(campaign.impressions || 0)}
        </div>
      </div>
      <div
        className={
          'flex flex-row justify-between items-center mx-2 font-Spartan my-2 pb-2  border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex gap-2 flex-row items-center ml-2'}>
          <div className={'flex justify-center items-center'}>
            <DataBar className={'h-5 w-5'} />
          </div>
          <div
            className={
              'flex mr-2 justify-start font-medium text-xs text-primary-xiketic'
            }>
            Clicks
          </div>
        </div>
        <div className={'flex mr-2 justify-end font-medium text-xs text-primary-xiketic'}>
          {formatNumber(campaign.clicks || 0)}
        </div>
      </div>
      <div className={'flex flex-col py-4 px-2 w-full'}>
        <Button
          onClick={() => {
            navigate(
              t('dealership.campaigns.self-service.report', {
                dealership,
                campaign: {
                  _id: campaign._id,
                },
              }),
            )
          }}
          disabled={campaign.campaign_status === 'pending'}
          animate={false}
          variant={'secondary'}
          rightSlot={
            <ChevronRight
              fill={campaign.campaign_status === 'pending' ? 'gainsboro-gray' : '#0932C2'}
            />
          }>
          See campaign
        </Button>
      </div>
    </div>
  )
}

const Managed = (props: {
  item: z.infer<typeof ManagedCampaignSchema>
  show_budget_type: number
}): JSX.Element => {
  const { item: campaign, show_budget_type } = props
  const navigate = useNavigate()
  const dealership = useDealership()
  const { t } = useTranslation('routes')

  const agencyId = useMemo(() => {
    return dealership?.organization_id
  }, [dealership?.organization_id])

  const dealershipId = useMemo(() => {
    return dealership?.id
  }, [dealership?.id])
  const { data: vehicles } = useGetVehiclesByCampaignId({
    campaign_id: campaign?.client_campaign_id,
  })

  return (
    <div
      className={classNames('flex flex-col border-2 rounded-lg font-Spartan p-4', {
        'bg-yellow-secondary/10 border-dashed border-yellow-secondary':
          campaign.campaign_status === 'pending',
      })}>
      <div className={'flex flex-row justify-between p-2 items-center'}>
        <div className={'flex flex-row items-center gap-3'}>
          <div
            className={'flex justify-center items-center bg-gray-200 rounded-full p-2'}>
            <Announce className="h-6 w-6" />
          </div>
          <span className={'font-bold'}>{campaign?.client_campaign_display_name}</span>
        </div>
        <div className="uppercase pl-5">
          <Pill type={campaign.campaign_status === 'active' ? 'success' : 'warning'}>
            {campaign.campaign_status}
          </Pill>
        </div>
      </div>
      <div className="flex flex-row justify-between items-center px-2 pb-2">
        <div className={'flex flex-row text-xs'}>
          {dayjs(campaign.start_date).format('MMMM D')} -{' '}
          {dayjs(campaign.end_date).format('MMMM D')}
        </div>
        {Boolean(show_budget_type) && (
          <div>
            <Pill type="warning">{`${
              show_budget_type === 1
                ? formatCurrency(campaign.demand_local_net)
                : formatCurrency(campaign.budget)
            }`}</Pill>
          </div>
        )}
      </div>
      <div
        className={
          'flex flex-row justify-between mx-2 font-Spartan my-2 pb-2 border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex flex-row items-center ml-2'}>
          <div className={'mr-2 justify-center items-center'}>
            <LinkIcon className={'fill-black stroke-black h-5 w-5'} />
          </div>
          <span className={'mr-2 justify-start font-medium text-xs text-primary-xiketic'}>
            Linked To
          </span>
        </div>
        <div className={'flex justify-end text-sm'}>
          {campaign.product_code === 'DYD' ? (
            <TradeDeskIcon className="h-[25px] w-[25px]" />
          ) : campaign.product_code === 'DYN' ||
            campaign.product_code === 'FDL' ||
            campaign.product_code === 'DSL' ? (
            <FBIcon className="h-[25px] w-[25px]" />
          ) : campaign.product_code === 'INV' ||
            campaign.product_code === 'VLA' ||
            campaign.product_code === 'TRV' ? (
            campaign.vendor === 'Bing' ? (
              <img
                className="h-[25px] w-[25px]"
                src={getImageURL('images/bing_ads_logo.png')}
              />
            ) : (
              <GoogleLogo className="h-[25px] w-[25px]" />
            )
          ) : campaign.product_code === 'SEM' ? (
            <>
              <GoogleLogo className="h-[25px] w-[25px]" />
              <img
                className="h-[25px] w-[25px]"
                src={getImageURL('images/bing_ads_logo.png')}
              />
            </>
          ) : campaign.product_code === 'AZD' || campaign.product_code === 'AZV' ? (
            <AmazonIcon className={'w-6 h-6'} />
          ) : (
            <DLLogo className="h-[25px] w-[25px]" />
          )}
        </div>
      </div>
      <div
        className={
          'flex flex-row justify-between items-center mx-2 font-Spartan items-center my-2 pb-2  border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex gap-2 flex-row items-center ml-2'}>
          <div className={'flex justify-center items center'}>
            {
              // show vehicles for campaigns with inventory
              campaign.product_code === 'DYD' ||
              campaign.product_code === 'DYN' ||
              campaign.product_code === 'INV' ||
              campaign.product_code === 'VLA' ||
              campaign.product_code === 'FDL' ? (
                <div className={'gap-1 flex flex-row items-center'}>
                  <Cars className="h-[25px] w-[25px]" />
                  <span className={'font-medium text-xs text-primary-xiketic'}>
                    Vehicles
                  </span>
                </div>
              ) : campaign.product_code === 'OTT' ||
                campaign.product_code === 'VID' ||
                campaign?.product_code === 'TRV' ||
                campaign.product_code === 'AZV' ? (
                <div className={'gap-1 flex flex-row items-center'}>
                  <DataBar className="h-[25px] w-[25px]" />
                  <span className={'font-medium text-xs text-primary-xiketic'}>
                    Views
                  </span>
                </div>
              ) : campaign.product_code === 'AUD' ? (
                <div className={'gap-1 flex flex-row items-center'}>
                  <DataBar className="h-[25px] w-[25px]" />
                  <span className={'font-medium text-xs text-primary-xiketic'}>
                    Listens
                  </span>
                </div>
              ) : campaign.product_code === 'DOH' ? (
                <div className={'gap-1 flex flex-row items-center'}>
                  <DataBar className="h-[25px] w-[25px]" />
                  <span className={'font-medium text-xs text-primary-xiketic'}>
                    Avg Audience Size
                  </span>
                </div>
              ) : (
                <div className={'gap-1 flex flex-row items-center'}>
                  <DataBar className="h-[25px] w-[25px]" />
                  <span className={'font-medium text-xs text-primary-xiketic'}>
                    Clicks
                  </span>
                </div>
              )
            }
          </div>
        </div>
        <span className={'mr-2 justify-end font-medium text-xs text-primary-xiketic'}>
          {
            // show vehicles for campaigns with inventory
            campaign.product_code === 'DYD' ||
            campaign.product_code === 'DYN' ||
            campaign.product_code === 'INV' ||
            campaign.product_code === 'VLA' ||
            campaign.product_code === 'FDL'
              ? formatNumber(vehicles?.length)
              : campaign.product_code === 'OTT' ||
                campaign.product_code === 'VID' ||
                campaign.product_code === 'TRV' ||
                campaign.product_code === 'AUD' ||
                campaign.product_code === 'AZV'
              ? formatNumber(campaign?.views)
              : campaign.product_code === 'DOH'
              ? formatNumber((campaign?.audience_impressions || 0) / campaign.impressions)
              : formatNumber(campaign?.clicks)
          }
        </span>
      </div>
      <div
        className={
          'flex flex-row justify-between items-center mx-2 font-Spartan my-2 pb-2  border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex gap-2 flex-row items-center ml-2'}>
          <div className={'flex justify-center items-center'}>
            <DataBar className="h-[25px] w-[25px]" />
          </div>
          <div
            className={
              'flex mr-2 justify-start font-medium text-xs text-primary-xiketic'
            }>
            Impressions
          </div>
        </div>
        <div className={'flex mr-2 justify-end font-medium text-xs text-primary-xiketic'}>
          {formatNumber(
            (campaign?.product_code === 'DOH'
              ? campaign?.audience_impressions
              : campaign?.impressions) || 0,
          )}
        </div>
      </div>
      <div
        className={
          'flex flex-row justify-between mx-2 items-center font-Spartan my-2 pb-2  border-b-2 border-gainsboro-gray'
        }>
        <div className={'flex gap-2 flex-row items-center ml-2'}>
          <div className={'flex justify-center items-center'}>
            <Product className={'h-5 w-5'} />
          </div>
          <div
            className={
              'flex mr-2 justify-start font-medium text-xs text-primary-xiketic'
            }>
            Product
          </div>
        </div>
        <div className={'flex mr-2 justify-end font-medium text-xs text-primary-xiketic'}>
          {campaign?.product_name}
        </div>
      </div>
      <div className={'flex flex-col py-4 px-2 w-full'}>
        <Button
          onClick={() => {
            navigate(
              t('dealership.campaigns.report', {
                dealership,
                campaign,
              }),
            )
          }}
          onContextMenu={() => {
            window.open(
              t('dealership.campaigns.report', {
                dealership,
                campaign,
              }),
              '_blank',
            )
          }}
          disabled={campaign.campaign_status === 'pending'}
          animate={false}
          variant={'secondary'}
          rightSlot={
            <ChevronRight
              fill={campaign.campaign_status === 'pending' ? 'gainsboro-gray' : '#0932C2'}
            />
          }>
          {campaign.campaign_status === 'pending'
            ? 'Campaign not started yet'
            : 'See Campaign Performance'}
        </Button>
      </div>
    </div>
  )
}

SelfService.displayName = 'SelfService'
Managed.displayName = 'Managed'

CampaignOverviewCard.SelfService = SelfService
CampaignOverviewCard.Managed = Managed

export default CampaignOverviewCard
